import { useMediaQuery } from 'react-responsive';

import type {
  ReeferMediaQueryDesktopSize,
  ReeferMediaQueryMobileSize,
  ReeferMediaQueryParam,
} from '../internal/utils/mediaQueries';
import { REEFER_MEDIA_QUERY_SIZES } from '../internal/utils/mediaQueries';
import { useIsHydrated } from './useIsHydrated';

export interface ReeferMediaQueryMobileProps {
  size?: ReeferMediaQueryMobileSize;
  width?: ReeferMediaQueryParam;
}

export interface ReeferMediaQueryDesktopProps {
  size?: ReeferMediaQueryDesktopSize;
  width?: ReeferMediaQueryParam;
}

export interface ReeferMediaQueryTabletProps {
  width?: ReeferMediaQueryParam;
}

/**
 * `react-responsive` seeds its state from `window.matchMedia` on the first
 * client render, which differs from the server (no window, always `false`) and
 * causes hydration mismatches. React uses the server snapshot while hydrating,
 * then re-renders with the client snapshot, so the first client render matches
 * the server HTML. Client-only renders (SPAs, tests) skip straight to `true`.
 */
const useHydrationSafeMediaQuery = (query: string) => {
  const matches = useMediaQuery({ query });
  const isHydrated = useIsHydrated();
  return isHydrated && matches;
};

/**
 * Media Query Hooks
 *
 * These are `false` during SSR and hydration. Prefer CSS media queries when
 * possible, since markup that depends on these will shift after hydration.
 */

export function useMobileMediaQuery({
  size,
  width = 'max',
}: ReeferMediaQueryMobileProps) {
  // defaults to 1px smaller than tablet
  const screenSize = size
    ? REEFER_MEDIA_QUERY_SIZES[`mobile-${size}`]
    : REEFER_MEDIA_QUERY_SIZES['tablet'];

  return useHydrationSafeMediaQuery(`(${width}-width: ${screenSize})`);
}

export function useTabletMediaQuery({
  width = 'min',
}: ReeferMediaQueryTabletProps) {
  return useHydrationSafeMediaQuery(
    `(${width}-width: ${REEFER_MEDIA_QUERY_SIZES['tablet']})`
  );
}

export function useDesktopMediaQuery({
  size = 'sm',
  width = 'min',
}: ReeferMediaQueryDesktopProps) {
  const screenSize = REEFER_MEDIA_QUERY_SIZES[`desktop-${size}`];

  return useHydrationSafeMediaQuery(`(${width}-width: ${screenSize})`);
}
